import { useEffect, useRef } from "react";
import { toast } from "@/components/Toaster";
import { usePermissions } from "@/hooks/usePermissions";
import type { WsClient } from "@/lib/ws-client";

/**
 * WU-1.5 — live ESCALATION_NEW consumer.
 *
 * The workflow engine publishes `{ type: "ESCALATION_NEW", documentId,
 * context, targetQueue }` on `escalation:<tenant>` and every authenticated
 * socket auto-subscribes (websocket.ts), but until this hook no web code
 * listened — escalations were published into the void and supervisors only
 * discovered them on the next dashboard poll/visit.
 *
 * For users holding `escalation_resolve` (the same permission the Queue
 * Dashboard gates its escalation panel on), a live event:
 *   1. toasts "Document escalated …" via the app-wide Toaster, and
 *   2. fires the optional `onEscalation` callback (the dashboard uses it to
 *      bump a reload token so the EscalationQueue badge/list refetch live).
 *
 * The permission is checked BEFORE any UI effect — non-permitted operators
 * get nothing (the event is a supervisor signal, not operator noise).
 */

/**
 * Build the toast line from an ESCALATION_NEW payload. `context.reason` is a
 * machine enum ("manual" | "field_rule" | ...), so the human text lives in
 * `context.triggers[0].message` (e.g. "Manually escalated" or the authored
 * rule message). The payload carries no filename. Optional chaining keeps
 * this safe on malformed frames without manual type-narrowing.
 */
export function escalationToastMessage(evt: unknown): string {
  const e = (evt ?? {}) as {
    targetQueue?: string;
    context?: { triggers?: Array<{ message?: string; ruleName?: string }> } | null;
  };
  const trigger = e.context?.triggers?.[0];
  const detail = trigger?.message || trigger?.ruleName || null;
  const queue = e.targetQueue ? ` to ${e.targetQueue}` : "";
  return detail
    ? `Document escalated${queue} — ${detail}`
    : `Document escalated${queue}`;
}

export function useEscalationAlerts(
  wsClient: WsClient | null,
  opts?: { onEscalation?: () => void },
): { permitted: boolean } {
  const { hasPermission, checkPermission } = usePermissions();

  // Warm the permission cache (same idiom as QueueDashboardPage /
  // EscalationQueue): checkPermission populates, hasPermission reads sync.
  useEffect(() => {
    void checkPermission("escalation_resolve");
  }, [checkPermission]);
  const permitted = hasPermission("escalation_resolve");

  // Refs keep the WS registration stable across permission/callback churn —
  // the handler always sees the latest values without re-subscribing.
  const permittedRef = useRef(permitted);
  permittedRef.current = permitted;
  const onEscalationRef = useRef(opts?.onEscalation);
  onEscalationRef.current = opts?.onEscalation;

  useEffect(() => {
    if (!wsClient) return;
    const off = wsClient.on("ESCALATION_NEW", (evt) => {
      // Permission gate FIRST — no toast, no refetch for non-supervisors.
      if (!permittedRef.current) return;
      toast.info(escalationToastMessage(evt));
      onEscalationRef.current?.();
    });
    return () => {
      off();
    };
  }, [wsClient]);

  return { permitted };
}
